import { makeObservable, observable, action } from 'mobx'
import type {
  MicContext,
  MicFailure,
  MicReport,
  SpeechDetection,
} from './types'

/**
 * The probe MicCheck reads to decide ready vs digital_silence, and whether a
 * live mic is loud enough to hear (near_silence).
 */
export type MicMeasurement = {
  // True when the current window carries no signal at all.
  isSilent: () => boolean
  // The current window's loudness, RMS on the 0-1 full-scale range.
  readLevel: () => number
  cleanup: () => void
}

// 1s: a quiet room's hiss is steady, so one analyser window a second shows the
// noise floor; speech is far louder than the threshold.
const LEVEL_SAMPLE_INTERVAL_MS = 1000

export type SpeechDetectorCleanup = () => void | Promise<void>

/**
 * Everything MicCheck needs from the outside, injected so the protocol can be
 * driven by a fake track and a stubbed worklet loader in tests, and by the real
 * analysers in the browser.
 */
export type MicCheckDeps = {
  // The digital-zero probe over the raw track. Throws only when the audio
  // context is unsupported.
  createMeasurement: (track: MediaStreamTrack) => MicMeasurement
  // The worklet speech detector. Resolves with a cleanup, or rejects when the
  // worklet fails to load — which sets speechDetection: 'unavailable', never a
  // silent microphone.
  createSpeechDetector: (
    track: MediaStreamTrack,
    onThresholdCross: (over: boolean) => void
  ) => Promise<SpeechDetectorCleanup>
  now: () => number
  setTimer: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimer: (handle: ReturnType<typeof setTimeout>) => void
  config: () => {
    micGraceMS: number
    // How long a track that has never appeared may stay missing before
    // no_track. The meeting mic publishes seconds after joining; a preview
    // track is handed over already captured.
    publishGraceMS: number
    silenceConfirmDelayMS: number
    silencePollIntervalMS: number
    nearSilenceRMS: number
    nearSilenceMS: number
  }
}

type CheckingReason =
  | 'starting'
  | 'changing_device'
  | 'reconnecting'
  | 'repairing'

/**
 * Whether two reports are identical — used to suppress redundant emits. Compares
 * per variant with `in`-narrowing (no casts, no reliance on object key order).
 */
function reportsEqual(a: MicReport, b: MicReport): boolean {
  if (a.state !== b.state) return false
  switch (a.state) {
    case 'inactive':
    case 'checking':
      return 'reason' in b && a.reason === b.reason
    case 'ready':
      return (
        'hasHeardSpeech' in b &&
        a.hasHeardSpeech === b.hasHeardSpeech &&
        a.speechDetection === b.speechDetection
      )
    case 'failed':
      return 'cause' in b && a.cause === b.cause
  }
}

/**
 * Says what the microphone is doing, and nothing else. It is told why audio is
 * on or off (`setContext`) and which underlying track to observe (`setTrack`);
 * it never reads MeetingCubit, never looks at slides, and never guesses intent
 * from a disabled track. Every new track is a new observation, and a generation
 * number rejects late asynchronous work from a previous one.
 */
export class MicCheck {
  report: MicReport = { state: 'checking', reason: 'starting' }

  private readonly deps: MicCheckDeps
  private context: MicContext = 'active'
  private track: MediaStreamTrack | null = null
  // Bumped on every input change so late async work (a worklet load, a read
  // callback) from a previous observation can recognise itself as stale.
  private generation = 0
  private disposed = false

  private measuring = false
  private measurement: MicMeasurement | null = null
  private speechCleanup: SpeechDetectorCleanup | null = null
  private speechDetection: SpeechDetection = 'available'
  private hasHeardSpeech = false
  private pendingSilenceConfirm = false
  private pendingRecoveryConfirm = false
  // When the level last dropped under nearSilenceRMS and stayed there; null
  // while the mic is loud enough.
  private quietSince: number | null = null
  private loudSamplesInRow = 0
  private publishRejected = false
  private checkingReason: CheckingReason = 'starting'

  private graceTimer: ReturnType<typeof setTimeout> | null = null
  private pollTimer: ReturnType<typeof setTimeout> | null = null
  private levelTimer: ReturnType<typeof setTimeout> | null = null

  constructor(deps: MicCheckDeps) {
    this.deps = deps
    makeObservable<MicCheck, 'setReport'>(this, {
      report: observable.ref,
      setReport: action,
    })
    this.evaluate()
  }

  setContext(context: MicContext): void {
    if (this.disposed || context === this.context) return
    this.generation++
    this.context = context
    this.evaluate()
  }

  /** A new track starts a new observation. */
  setTrack(track: MediaStreamTrack | null): void {
    if (this.disposed) return
    this.generation++
    const hadTrack = this.track !== null
    this.teardownMeasurement()
    this.clearGraceTimer()
    this.track = track
    this.publishRejected = false
    this.hasHeardSpeech = false
    this.speechDetection = 'available'
    this.pendingSilenceConfirm = false
    this.pendingRecoveryConfirm = false
    this.checkingReason = hadTrack ? 'changing_device' : 'starting'
    this.evaluate()
  }

  /** A rejected publish is no_track at once, no grace. */
  reportPublishFailed(): void {
    if (this.disposed) return
    this.generation++
    this.teardownMeasurement()
    this.clearGraceTimer()
    this.track = null
    this.publishRejected = true
    this.evaluate()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    this.teardownMeasurement()
    this.clearGraceTimer()
  }

  private setReport(report: MicReport): void {
    // Only churn the observable on a genuine change. A poll read re-derives the
    // same `ready` report every interval; without this, the observable (and so
    // the reaction that logs soundcheck_mic_result, and the UI) would fire on
    // every tick instead of on state changes.
    if (reportsEqual(this.report, report)) return
    this.report = report
  }

  private classifyUnusable(): MicFailure | null {
    if (this.track === null) return 'no_track'
    if (this.track.readyState === 'ended') return 'track_stopped'
    // The SDK disabled the publication without the user asking; user intent
    // arrives as context 'user_muted', never as a disabled track.
    if (!this.track.enabled) return 'track_stopped'
    return null
  }

  private evaluate(): void {
    if (this.disposed) return

    switch (this.context) {
      case 'user_muted':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'inactive', reason: 'user_muted' })
        return
      case 'not_required':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'inactive', reason: 'not_required' })
        return
      case 'reconnecting':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'checking', reason: 'reconnecting' })
        return
      case 'repairing':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'checking', reason: 'repairing' })
        return
      case 'active':
        break
    }

    if (this.publishRejected) {
      this.teardownMeasurement()
      this.clearGraceTimer()
      this.setReport({ state: 'failed', cause: 'no_track' })
      return
    }

    const unusable = this.classifyUnusable()
    if (unusable === null) {
      // A live, enabled track: measure it. Measurement keeps running through a
      // digital_silence failure so a later non-zero read can recover.
      this.clearGraceTimer()
      this.ensureMeasuring()
      return
    }

    // A dead track cannot be measured for readiness. Give it grace to start or
    // restart, then confirm the failure.
    this.teardownMeasurement()
    this.startGraceIfNeeded(unusable)
  }

  private startGraceIfNeeded(cause: MicFailure): void {
    if (this.report.state === 'failed' && this.report.cause === cause) return
    if (this.graceTimer !== null) return
    this.setReport({ state: 'checking', reason: this.checkingReason })
    const config = this.deps.config()
    const awaitingFirstTrack =
      cause === 'no_track' && this.checkingReason === 'starting'
    const graceMS = awaitingFirstTrack
      ? config.publishGraceMS
      : config.micGraceMS
    this.graceTimer = this.deps.setTimer(() => {
      this.graceTimer = null
      if (this.disposed || this.context !== 'active') {
        this.evaluate()
        return
      }
      const stillUnusable = this.classifyUnusable()
      if (this.publishRejected) {
        this.setReport({ state: 'failed', cause: 'no_track' })
      } else if (stillUnusable) {
        this.setReport({ state: 'failed', cause: stillUnusable })
      } else {
        this.evaluate()
      }
    }, graceMS)
  }

  private ensureMeasuring(): void {
    if (this.measuring || this.track === null) return
    this.measuring = true
    const track = this.track
    const gen = this.generation

    try {
      this.measurement = this.deps.createMeasurement(track)
    } catch {
      // No probe means we cannot prove silence; treat the track as ready with
      // speech detection unavailable rather than manufacture a failure.
      this.measurement = null
      this.speechDetection = 'unavailable'
      this.setReadyReport()
    }

    this.deps
      .createSpeechDetector(track, (over) => this.onThresholdCross(gen, over))
      .then((cleanup) => {
        if (gen !== this.generation) {
          void cleanup()
          return
        }
        this.speechCleanup = cleanup
        this.speechDetection = 'available'
        this.refreshReadyReport()
      })
      .catch(() => {
        if (gen !== this.generation) return
        this.speechDetection = 'unavailable'
        this.refreshReadyReport()
      })

    if (this.report.state !== 'ready') {
      this.setReport({ state: 'checking', reason: this.checkingReason })
    }
    if (this.measurement) {
      // Let the mic warm up before the first read; the analyser buffer starts at
      // the zero line and would read silent immediately.
      this.scheduleRead(this.deps.config().micGraceMS)
      this.scheduleLevelSample()
    }
  }

  private scheduleRead(delay: number): void {
    if (this.pollTimer !== null) this.deps.clearTimer(this.pollTimer)
    this.pollTimer = this.deps.setTimer(() => {
      this.pollTimer = null
      this.performRead()
    }, delay)
  }

  private performRead(): void {
    if (this.disposed || !this.measuring || this.measurement === null) return
    const { silenceConfirmDelayMS, silencePollIntervalMS } = this.deps.config()
    const silent = this.measurement.isSilent()
    if (!silent) {
      this.pendingSilenceConfirm = false
      // A near-silent mic reads non-zero; only the level sampling may clear it.
      if (this.isFailedWith('near_silence')) {
        this.scheduleRead(silencePollIntervalMS)
        return
      }
      const recovering = this.isFailedWith('digital_silence')
      if (recovering && !this.pendingRecoveryConfirm) {
        // One non-zero read is not recovery: a dead mic that leaks a single
        // blip flipped failed/ready four times in one session. Confirm it.
        this.pendingRecoveryConfirm = true
        this.scheduleRead(silenceConfirmDelayMS)
        return
      }
      this.pendingRecoveryConfirm = false
      this.setReadyReport()
      this.scheduleRead(silencePollIntervalMS)
      return
    }
    this.pendingRecoveryConfirm = false
    if (this.pendingSilenceConfirm) {
      this.pendingSilenceConfirm = false
      this.setReport({ state: 'failed', cause: 'digital_silence' })
      // Keep polling: a mic that comes back non-zero recovers.
      this.scheduleRead(silencePollIntervalMS)
      return
    }
    // First silent read: keep the current report and take a confirming read.
    this.pendingSilenceConfirm = true
    this.scheduleRead(silenceConfirmDelayMS)
  }

  private scheduleLevelSample(): void {
    if (this.levelTimer !== null) this.deps.clearTimer(this.levelTimer)
    this.levelTimer = this.deps.setTimer(() => {
      this.levelTimer = null
      this.sampleLevel()
    }, LEVEL_SAMPLE_INTERVAL_MS)
  }

  /**
   * A live mic that never gets louder than a faint hiss for nearSilenceMS is
   * near_silence: it passes the digital-zero test, so it read ready while the
   * server received packets at maxRMS 0.0001 for a whole session. Recovery
   * takes two loud samples in a row, like the other reads.
   */
  private sampleLevel(): void {
    if (this.disposed || !this.measuring || this.measurement === null) return
    const { nearSilenceRMS, nearSilenceMS } = this.deps.config()
    const now = this.deps.now()
    const quiet = this.measurement.readLevel() < nearSilenceRMS
    if (quiet) {
      this.loudSamplesInRow = 0
      if (this.quietSince === null) this.quietSince = now
      const quietLongEnough = now - this.quietSince >= nearSilenceMS
      // Only a mic that reads ready: a digitally silent one already failed.
      if (quietLongEnough && this.report.state === 'ready') {
        this.setReport({ state: 'failed', cause: 'near_silence' })
      }
    } else {
      this.quietSince = null
      this.loudSamplesInRow++
      if (this.isFailedWith('near_silence') && this.loudSamplesInRow >= 2) {
        this.setReadyReport()
      }
    }
    this.scheduleLevelSample()
  }

  private isFailedWith(cause: MicFailure): boolean {
    return this.report.state === 'failed' && this.report.cause === cause
  }

  private setReadyReport(): void {
    this.setReport({
      state: 'ready',
      hasHeardSpeech: this.hasHeardSpeech,
      speechDetection: this.speechDetection,
    })
  }

  private refreshReadyReport(): void {
    if (this.report.state === 'ready') this.setReadyReport()
  }

  private onThresholdCross(gen: number, over: boolean): void {
    if (this.disposed || gen !== this.generation) return
    if (!over || this.hasHeardSpeech) return
    this.hasHeardSpeech = true
    this.refreshReadyReport()
  }

  private teardownMeasurement(): void {
    this.measuring = false
    this.pendingSilenceConfirm = false
    this.pendingRecoveryConfirm = false
    if (this.pollTimer !== null) {
      this.deps.clearTimer(this.pollTimer)
      this.pollTimer = null
    }
    if (this.levelTimer !== null) {
      this.deps.clearTimer(this.levelTimer)
      this.levelTimer = null
    }
    // Muting or swapping the track starts the quiet period over.
    this.quietSince = null
    this.loudSamplesInRow = 0
    if (this.measurement) {
      this.measurement.cleanup()
      this.measurement = null
    }
    if (this.speechCleanup) {
      void this.speechCleanup()
      this.speechCleanup = null
    }
  }

  private clearGraceTimer(): void {
    if (this.graceTimer !== null) {
      this.deps.clearTimer(this.graceTimer)
      this.graceTimer = null
    }
  }
}
